import { useCallback, useEffect, useMemo, useState } from 'react';
import { qualifiesForViralSettings } from '../../analytics';
import {
	isAcceptableFailure,
	isAcceptableFailureForDirectAccess,
	isForbidden,
	makeAri,
	resolveError,
	type Result,
	waitForAllPromises,
} from '../../utils';
import {
	createHISIDomainFlagKey,
	createHISIOpenInviteFlagKey,
	defaultInviteApiClient,
	defaultOpenInviteClient,
} from '../../clients';
import {
	type GetDirectAccessSettingFailureResponseValue,
	type GetDirectAccessSettingRequest,
	type GetDirectAccessSettingSuccessResponseValue,
	type GetOpenInviteInfo,
	type GetOpenInviteStateResponse,
	type ViralSettingsByDomain,
	type ViralSettingsByDomainValueObject,
	type ViralSettingsDESEligibleStates,
	type ViralSettingsHookProps,
} from './types';

import {
	createViralSettingsByDomainValueObject,
	createViralSettingsMapWithDefaultValues,
	getDESEligibleDomainsStates,
	getDomainFromResults,
	getViralSettingsByDomainValueObjectFromResponse,
	handlePromise,
} from './utils';
import { defaultViralSettingsHisiClient, type WasExposed } from './ViralSettingsHisiClient';
import { useAnalytics } from '../../analytics';
import { ViralSettingsAnalytics } from './ViralSettingsAnalytics';

const EMPTY_DOMAINS = {};

// Custom Hook that keeps track of all data required for the viral settings experiments.
export const useViralSettings = (props: ViralSettingsHookProps) => {
	const { domains, cloudId, products, role, selectHasBeenFocused, emails, disableViralSettings } =
		props;

	const analytics = useAnalytics(ViralSettingsAnalytics);
	const [enableOpenInvite, setEnableOpenInvite] = useState<boolean>(false);

	const [showOpenInvite, setShowOpenInvite] = useState<boolean>(false);
	const [viralSettingsByDomain, setViralSettingsByDomain] = useState<ViralSettingsByDomain>({});
	const [viralSettingsDESEligibleStates, setViralSettingsDESEligibleStates] =
		useState<ViralSettingsDESEligibleStates>({ checked: [], unchecked: [] });

	const product: string | undefined = products && products[0];

	const isViralSettingsEnabled = useMemo(() => {
		return (
			!disableViralSettings &&
			qualifiesForViralSettings(role) &&
			products &&
			products.length === 1 &&
			(selectHasBeenFocused || emails.filter((v) => v).length > 0)
		);
	}, [products, selectHasBeenFocused, emails, role, disableViralSettings]);

	const eligibleDomains = useMemo(() => {
		return Object.keys(viralSettingsByDomain).filter(
			(domain) => viralSettingsByDomain[domain].desPromotionEligible,
		);
	}, [viralSettingsByDomain]);

	//Clear cache on render.
	useEffect(() => {
		defaultInviteApiClient.clearDESCache();
		defaultOpenInviteClient.clearCache();
		defaultViralSettingsHisiClient.clearCache();
	}, []);

	const getUserInvitesSetting = useCallback(
		async (product: string, cloudId: string): Promise<GetOpenInviteInfo | void> => {
			const ari = makeAri(product, cloudId);
			try {
				const { response, cached } = (await defaultOpenInviteClient.getOpenInviteState(
					product,
					cloudId,
				)) as GetOpenInviteStateResponse;
				if (!cached) {
					analytics.getUserInvitesSettingsSucceededSLO(ari);
				}
				return response;
			} catch (err: unknown) {
				const { errorMessage, responseCode } = resolveError(err);
				analytics.getUserInvitesSettingsFailure({
					ari,
					errorMessage,
					responseCode,
				});
				// in the case of the response being Forbidden, neither an SLO success or error should be fired
				if (!isForbidden(responseCode)) {
					if (isAcceptableFailure(errorMessage, responseCode)) {
						analytics.getUserInvitesSettingsSucceededSLO(ari);
					} else {
						analytics.getUserInvitesSettingsFailureSLO({
							ari,
							errorMessage,
							responseCode,
						});
					}
				}
			}
		},
		[analytics],
	);

	const getDirectAccessEligibility = useCallback(
		async (
			request: GetDirectAccessSettingRequest,
		): Promise<
			GetDirectAccessSettingSuccessResponseValue | GetDirectAccessSettingFailureResponseValue
		> => {
			try {
				const response = await defaultInviteApiClient.getDirectAccessSetting(request);
				const { response: fetchedResponse, cached } = response;
				if (!cached) {
					analytics.getDirectAccessSettingSucceededSLO(request);
				}
				return {
					...fetchedResponse,
					desPromotionEligible:
						'desPromotionEligible' in fetchedResponse && fetchedResponse?.desPromotionEligible
							? fetchedResponse.desPromotionEligible
							: false,
				};
			} catch (err: unknown) {
				const { responseCode, errorMessage } = resolveError(err);

				const attributes = {
					domain: request.domain,
					responseCode,
					errorMessage,
				};
				analytics.getDirectAccessSettingFailure(request, attributes);

				const isSuccess =
					isAcceptableFailureForDirectAccess(errorMessage, responseCode) ||
					isAcceptableFailure(errorMessage, responseCode);
				// in the case of the response being Forbidden, neither an SLO success or error should be fired
				if (!isForbidden(responseCode)) {
					if (isSuccess) {
						analytics.getDirectAccessSettingSucceededSLO(request);
					} else {
						analytics.getDirectAccessSettingFailureSLO(request, attributes);
					}
				}
				return { domain: request.domain, errorMessage };
			}
		},
		[analytics],
	);

	// Fetches all domain settings in parallel and return the resolved responses
	const getDomainViralSettings = useCallback(
		(product: string, cloudId: string) =>
			waitForAllPromises<
				GetDirectAccessSettingSuccessResponseValue | GetDirectAccessSettingFailureResponseValue
			>([
				...domains.map((domain) => {
					const request: GetDirectAccessSettingRequest = {
						domain,
						productAri: makeAri(product, cloudId),
						setting: 'DIRECT_ACCESS',
					};
					return getDirectAccessEligibility(request);
				}),
			]),
		[domains, getDirectAccessEligibility],
	);

	// Gets the default value for the open invite checkbox. Uses HISI and fallbacks to true
	const userInviteDefaultValue = useCallback(
		async (product: string, cloudId: string, shouldShowOpenInvite: boolean) => {
			// if the invite checkbox isnt being shown it should default to false
			if (!shouldShowOpenInvite) {
				return false;
			}

			return defaultViralSettingsHisiClient
				.getFlagWithAnalytics(
					{
						flagKey: createHISIOpenInviteFlagKey(product, cloudId),
						product,
					},
					analytics,
				)
				.then(
					(wasExposed: WasExposed) => {
						// Default value is true if this is their first exposure, and false if we know they have been previously exposed.
						return !wasExposed;
					},
					(_) => {
						// Will default to checked in the case that HISI fails
						return true;
					},
				);
		},
		[analytics],
	);

	// Get the default value for a domain using HISI, fallbacks to isChecked true if elligible for promotion
	const getDesDefaultValue = useCallback(
		async (
			product: string,
			cloudId: string,
			domainResponse: Result<
				GetDirectAccessSettingSuccessResponseValue,
				GetDirectAccessSettingFailureResponseValue
			>,
		): Promise<ViralSettingsByDomainValueObject> => {
			const domain = getDomainFromResults(domainResponse);

			// in the case that the domain is eligible for promotion
			if (
				domainResponse.status === 'fulfilled' &&
				domain &&
				(domainResponse.value as GetDirectAccessSettingSuccessResponseValue).desPromotionEligible
			) {
				const desPromotionEligible = (
					domainResponse.value as GetDirectAccessSettingSuccessResponseValue
				).desPromotionEligible;

				return defaultViralSettingsHisiClient
					.getFlagWithAnalytics(
						{
							flagKey: createHISIDomainFlagKey(product, cloudId, domain),
							product,
							domain,
						},
						analytics,
					)
					.then(
						(wasExposed) => {
							// Default value is true if this is their first exposure, and false if we know they have been previously exposed.
							return createViralSettingsByDomainValueObject(
								domain,
								!wasExposed,
								desPromotionEligible,
							);
						},
						(_) => {
							// If HISI unavailable assume this is their first exposure and return isCheck = true
							return createViralSettingsByDomainValueObject(domain, true, desPromotionEligible);
						},
					);
			} else {
				// is not eligible for promotion, do not show checkbox (desPromotion = false) and set is checked to false for safety
				return createViralSettingsByDomainValueObject(domain, false, false);
			}
		},
		[analytics],
	);

	// Gets all the HISI settings (open invite and domain) in parallel and returns in an array
	const getHISIDomainSettings = useCallback(
		(
			domainResponses: Result<
				GetDirectAccessSettingSuccessResponseValue,
				GetDirectAccessSettingFailureResponseValue
			>[],
		) =>
			waitForAllPromises<ViralSettingsByDomainValueObject>([
				...domainResponses.map((domainResponse) => {
					return getDesDefaultValue(product, cloudId, domainResponse);
				}),
			]),
		[cloudId, product, getDesDefaultValue],
	);

	const handleShouldShowOpenInvite = useCallback(async () => {
		const [openInviteState, error] = await handlePromise(getUserInvitesSetting(product, cloudId));
		const shouldShowOpenInvite =
			openInviteState &&
			openInviteState.getOpenInvite &&
			openInviteState.mode === 'REQUEST_ACCESS' &&
			!error;

		setShowOpenInvite(shouldShowOpenInvite);
	}, [product, cloudId, getUserInvitesSetting]);

	const handleDomains = useCallback(async () => {
		const domainResults: Result<
			GetDirectAccessSettingSuccessResponseValue,
			GetDirectAccessSettingFailureResponseValue
		>[] = await getDomainViralSettings(product, cloudId);
		const domainResultsWithDefaultValue = await getHISIDomainSettings(domainResults);

		const viralSettingsByDomainValueObjectFromResponse =
			getViralSettingsByDomainValueObjectFromResponse(domainResultsWithDefaultValue);
		setViralSettingsByDomain((previousViralSettingMap) => {
			// Create a map using the completed domain object with defaultValues
			const viralSettingsMap = createViralSettingsMapWithDefaultValues(
				previousViralSettingMap,
				viralSettingsByDomainValueObjectFromResponse,
			);
			setViralSettingsDESEligibleStates(getDESEligibleDomainsStates(viralSettingsMap));
			return viralSettingsMap;
		});
	}, [cloudId, getHISIDomainSettings, getDomainViralSettings, product]);

	const handleViralSettingsByDomainCheckbox = (newDomainState: ViralSettingsByDomain) => {
		setViralSettingsByDomain(newDomainState);
		setViralSettingsDESEligibleStates(getDESEligibleDomainsStates(newDomainState));
	};

	// useEffect to determine whether or not viral settings section should be displayed
	// triggers fetch calls to HISI if it should be displayed
	useEffect(() => {
		// reset value to false on change
		if (!isViralSettingsEnabled) {
			setEnableOpenInvite(false);
			setViralSettingsByDomain(EMPTY_DOMAINS);
		}
		if (isViralSettingsEnabled) {
			handleShouldShowOpenInvite();
			handleDomains();
		}
	}, [isViralSettingsEnabled, handleShouldShowOpenInvite, handleDomains]);

	// Determine initial state of open invite checkbox when showOpenInvite is updated.
	useEffect(() => {
		const handleDefaultOpenInvite = async () => {
			// Does not need error handled as userInviteDefaultValue catches the error
			const openInviteDefaultValue = await userInviteDefaultValue(product, cloudId, showOpenInvite);
			// will be default to undefined if shouldShowOpenInvite is false
			setEnableOpenInvite(openInviteDefaultValue);
		};
		handleDefaultOpenInvite();
	}, [showOpenInvite, product, cloudId, userInviteDefaultValue]);

	const responseObject = {
		viralSettingsByDomain,
		eligibleDomains,
		viralSettingsDESEligibleStates,
		handleViralSettingsByDomainCheckbox,
		setEnableOpenInvite,
		isViralSettingsEnabled,
		showOpenInvite,
		enableOpenInvite,
	};
	return responseObject;
};
